import { createFileRoute, Link } from '@tanstack/react-router';
import {
  BookOpen,
  ChevronRight,
  FileText,
  Layers,
  LayoutTemplate,
  Palette,
} from 'lucide-react';
import { DocsLayout } from './-components/DocsLayout';
import {
  componentCategories,
  foundationItems,
  guideItems,
  patternCategories,
} from './-registry';

export const Route = createFileRoute('/docs/')({
  component: DocsLanding,
});

function DocsLanding() {
  return (
    <DocsLayout>
      <div className='mb-10'>
        <h1 className='text-3xl font-bold text-foreground tracking-tight mb-3'>
          Retraced UI Docs
        </h1>
        <p className='text-sm text-muted-foreground leading-7 max-w-xl'>
          Component reference and guides for building pages in{' '}
          <strong className='text-foreground'>frontend-v2</strong>. Every shared
          component has live examples, prop tables, and copy-paste code.
        </p>
      </div>

      {/* Guides */}
      <section className='mb-10'>
        <div className='flex items-center gap-2 mb-4'>
          <BookOpen className='size-4 text-muted-foreground' />
          <h2 className='text-sm font-semibold text-foreground'>Guides</h2>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
          {guideItems.map(item => (
            <CardLink
              key={item.slug}
              href={`/docs/guides/${item.slug}`}
              title={item.name}
              description={item.description}
            />
          ))}
        </div>
      </section>

      {/* Foundations */}
      {foundationItems.length > 0 && (
        <section className='mb-10'>
          <div className='flex items-center gap-2 mb-4'>
            <Palette className='size-4 text-muted-foreground' />
            <h2 className='text-sm font-semibold text-foreground'>
              Foundations
            </h2>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            {foundationItems.map(item => (
              <CardLink
                key={item.slug}
                href={`/docs/foundations/${item.slug}`}
                title={item.name}
                description={item.description}
              />
            ))}
          </div>
        </section>
      )}

      {/* Components by category */}
      <section className='mb-10'>
        <div className='flex items-center gap-2 mb-4'>
          <Layers className='size-4 text-muted-foreground' />
          <h2 className='text-sm font-semibold text-foreground'>Components</h2>
        </div>
        <div className='space-y-8'>
          {componentCategories.map(cat => (
            <div key={cat.label}>
              <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 mb-3'>
                {cat.label}
              </p>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                {cat.items.map(item => (
                  <CardLink
                    key={item.slug}
                    href={`/docs/components/${item.slug}`}
                    title={item.name}
                    description={item.description}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Patterns */}
      {patternCategories.length > 0 && (
        <section>
          <div className='flex items-center gap-2 mb-4'>
            <LayoutTemplate className='size-4 text-muted-foreground' />
            <h2 className='text-sm font-semibold text-foreground'>Patterns</h2>
          </div>
          <div className='space-y-8'>
            {patternCategories.map(cat => (
              <div key={cat.label}>
                <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 mb-3'>
                  {cat.label}
                </p>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  {cat.items.map(item => (
                    <CardLink
                      key={item.slug}
                      href={`/docs/patterns/${item.slug}`}
                      title={item.name}
                      description={item.description}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </DocsLayout>
  );
}

function CardLink({
  href,
  title,
  description,
}: {
  href: string;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={href as never}
      className='group flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-4 hover:border-primary/40 hover:bg-primary/[0.03] transition-colors'
    >
      <div className='flex items-start gap-2.5 min-w-0'>
        <FileText className='size-4 text-muted-foreground/50 shrink-0 mt-0.5' />
        <div className='min-w-0'>
          <p className='text-sm font-medium text-foreground mb-0.5'>{title}</p>
          <p className='text-xs text-muted-foreground leading-relaxed'>
            {description}
          </p>
        </div>
      </div>
      <ChevronRight className='size-4 text-muted-foreground/40 group-hover:text-primary shrink-0 mt-0.5 transition-colors' />
    </Link>
  );
}
